import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Wrench, CheckCircle2, Clock, AlertTriangle,
  MessageSquare, Send, FileText, Building2,
  Loader2, RefreshCw, Shield, Calendar, Camera, Upload, Check, Circle
} from 'lucide-react';

const SEVERITY_COLORS = {
  blocker: 'bg-red-100 text-red-800', major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800', observation: 'bg-slate-100 text-slate-600',
};
const STATUS_COLORS = {
  assigned: 'bg-blue-100 text-blue-700', acknowledged: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-cyan-100 text-cyan-700', rectified_by_subcontractor: 'bg-teal-100 text-teal-700',
  awaiting_contractor_check: 'bg-amber-100 text-amber-700', closed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700', open: 'bg-red-100 text-red-700',
};
const DOC_STATUS_COLORS = {
  required: 'bg-amber-100 text-amber-700', requested: 'bg-blue-100 text-blue-700',
  submitted: 'bg-purple-100 text-purple-700', approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const COMPLETED_STATUSES = ['rectified_by_subcontractor', 'awaiting_contractor_check', 'closed'];

const CloseoutPortalPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('defects');
  const [showComment, setShowComment] = useState(false);
  const [activeDefect, setActiveDefect] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showRectify, setShowRectify] = useState(false);
  const [rectifyDefect, setRectifyDefect] = useState(null);
  const [rectifyComment, setRectifyComment] = useState('');
  const [rectifyPhoto, setRectifyPhoto] = useState(null);
  const [rectifyUploading, setRectifyUploading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/closeout/public/portal/${token}`);
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) setError('This portal link has expired. Please contact the project team for a new link.');
      else if (status === 404) setError('Invalid portal link. Please check the URL or contact the project team.');
      else setError('Failed to load portal. Please try again later.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAcknowledge = async (defectId) => {
    setActionLoading(defectId);
    try {
      await api.put(`/closeout/public/portal/${token}/defects/${defectId}/acknowledge`);
      toast.success('Defect acknowledged');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot acknowledge'); }
    finally { setActionLoading(null); }
  };

  const openRectifyDialog = (defect) => {
    setRectifyDefect(defect);
    setRectifyComment('');
    setRectifyPhoto(null);
    setShowRectify(true);
  };

  const handleRectifySubmit = async () => {
    if (!rectifyDefect) return;
    setRectifyUploading(true);
    try {
      let photoUrl = null;
      // Upload photo if provided
      if (rectifyPhoto) {
        const formData = new FormData();
        formData.append('file', rectifyPhoto);
        try {
          const uploadRes = await api.post(`/closeout/public/portal/${token}/defects/${rectifyDefect.id}/upload-photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          photoUrl = uploadRes.data?.photo_url;
        } catch { /* photo upload is best-effort */ }
      }
      // Submit comment if provided
      if (rectifyComment.trim()) {
        const commentWithPhoto = rectifyComment + (photoUrl ? `\n[Photo proof attached]` : '');
        await api.post(`/closeout/public/portal/${token}/defects/${rectifyDefect.id}/comment?comment_text=${encodeURIComponent(commentWithPhoto)}`);
      }
      // Mark as rectified
      await api.put(`/closeout/public/portal/${token}/defects/${rectifyDefect.id}/rectify`);
      toast.success('Marked as rectified — the project team has been notified');
      setShowRectify(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot mark as rectified'); }
    finally { setRectifyUploading(false); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !activeDefect) return;
    try {
      await api.post(`/closeout/public/portal/${token}/defects/${activeDefect}/comment?comment_text=${encodeURIComponent(commentText)}`);
      toast.success('Comment submitted');
      setShowComment(false);
      setCommentText('');
    } catch { toast.error('Failed to submit comment'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1e3a5f] mx-auto mb-4" />
          <p className="text-slate-500">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Portal Access Error</h1>
          <p className="text-slate-600">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  const { portal, project, defects, documents, stats } = data;
  const outstanding = defects.filter(d => !COMPLETED_STATUSES.includes(d.status));
  const completed = defects.filter(d => COMPLETED_STATUSES.includes(d.status));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="closeout-portal-page">
      {/* Header */}
      <div className="bg-[#0c2340] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-white/10">
                <Wrench className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold">Defect Rectification Portal</h1>
                <p className="text-sm text-blue-300">{project?.name || 'Project'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{portal.company_name}</p>
                <p className="text-xs text-blue-300/70">Expires: {portal.expires_at?.slice(0, 10)}</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadData} className="border-blue-400/30 text-blue-200 hover:text-white hover:border-blue-300">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="sm:hidden mt-3 flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-blue-200"><Building2 className="h-4 w-4" />{portal.company_name}</span>
            <span className="text-xs text-blue-300/70">Expires: {portal.expires_at?.slice(0, 10)}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
        {/* Progress Summary */}
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Your Progress</h2>
            <span className="text-xs text-slate-400">{completed.length} of {defects.length} complete</span>
          </div>
          <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#1e3a5f] rounded-full transition-all duration-500" style={{ width: `${defects.length > 0 ? (completed.length / defects.length) * 100 : 0}%` }} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
          <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold">{stats.total_defects}</p><p className="text-[9px] text-slate-500 uppercase">Total</p></CardContent></Card>
          <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-blue-600">{stats.pending_ack}</p><p className="text-[9px] text-slate-500 uppercase">Awaiting</p></CardContent></Card>
          <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-cyan-600">{stats.in_progress}</p><p className="text-[9px] text-slate-500 uppercase">In Progress</p></CardContent></Card>
          <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-teal-600">{stats.rectified}</p><p className="text-[9px] text-slate-500 uppercase">Rectified</p></CardContent></Card>
          <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-green-600">{stats.closed}</p><p className="text-[9px] text-slate-500 uppercase">Closed</p></CardContent></Card>
          <Card><CardContent className="p-2.5 text-center"><p className="text-xl font-bold text-red-600">{stats.rejected}</p><p className="text-[9px] text-slate-500 uppercase">Rejected</p></CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 mb-4">
            <TabsTrigger value="defects" className="gap-1.5"><Wrench className="h-4 w-4" /> Outstanding ({outstanding.length})</TabsTrigger>
            <TabsTrigger value="completed" className="gap-1.5"><CheckCircle2 className="h-4 w-4" /> Completed ({completed.length})</TabsTrigger>
            <TabsTrigger value="documents" className="gap-1.5"><FileText className="h-4 w-4" /> Documents ({documents?.length || 0})</TabsTrigger>
          </TabsList>

          {/* Outstanding Defects */}
          <TabsContent value="defects" className="space-y-2">
            {outstanding.length === 0 ? (
              <Card><CardContent className="p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-600">All items complete!</p>
                <p className="text-sm text-slate-400 mt-1">You have no outstanding defects</p>
              </CardContent></Card>
            ) : outstanding.map(d => (
              <Card key={d.id} className="hover:shadow-sm transition-shadow" data-testid={`portal-defect-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Tick circle */}
                    <div className="mt-0.5 shrink-0">
                      <Circle className="h-5 w-5 text-slate-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className="font-mono text-xs text-slate-400">{d.defect_no}</span>
                        <Badge className={`text-[10px] ${SEVERITY_COLORS[d.severity]}`}>{d.severity}</Badge>
                        <Badge className={`text-[10px] ${STATUS_COLORS[d.status] || 'bg-slate-100'}`}>{d.status?.replace(/_/g, ' ')}</Badge>
                        {d.completion_blocker_flag && <Badge className="bg-red-50 text-red-600 text-[9px] gap-0.5"><AlertTriangle className="h-2.5 w-2.5" /> Blocker</Badge>}
                      </div>
                      <p className="text-sm font-medium text-slate-800">{d.description_short}</p>
                      {d.description_long && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{d.description_long}</p>}
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-slate-500">
                        {d.location_text && <span>{d.location_text}</span>}
                        {d.trade_code && <span className="capitalize">{d.trade_code.replace(/_/g, ' ')}</span>}
                        {d.target_rectification_date && (
                          <span className={`flex items-center gap-1 ${d.target_rectification_date < new Date().toISOString().split('T')[0] ? 'text-red-500 font-medium' : ''}`}>
                            <Calendar className="h-3 w-3" /> Due: {d.target_rectification_date}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                      {d.status === 'assigned' && (
                        <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => handleAcknowledge(d.id)} disabled={actionLoading === d.id} data-testid={`portal-ack-${d.id}`}>
                          {actionLoading === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Acknowledge
                        </Button>
                      )}
                      {['acknowledged', 'in_progress', 'rejected'].includes(d.status) && (
                        <Button size="sm" className="h-8 text-xs bg-[#1e3a5f] hover:bg-[#16304d] gap-1" onClick={() => openRectifyDialog(d)} disabled={actionLoading === d.id} data-testid={`portal-rectify-${d.id}`}>
                          <Camera className="h-3 w-3" /> Rectify & Upload
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { setActiveDefect(d.id); setShowComment(true); }} data-testid={`portal-comment-${d.id}`}>
                        <MessageSquare className="h-3 w-3" /> Comment
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Completed Defects */}
          <TabsContent value="completed" className="space-y-2">
            {completed.length === 0 ? (
              <Card><CardContent className="p-12 text-center">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-500">No completed items yet</p>
              </CardContent></Card>
            ) : completed.map(d => (
              <Card key={d.id} className="bg-green-50/30 border-green-100" data-testid={`portal-completed-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className="font-mono text-xs text-slate-400">{d.defect_no}</span>
                        <Badge className={`text-[10px] ${STATUS_COLORS[d.status]}`}>{d.status?.replace(/_/g, ' ')}</Badge>
                      </div>
                      <p className="text-sm font-medium text-slate-700 line-through decoration-green-400">{d.description_short}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        {d.location_text && <span>{d.location_text}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-2">
            {(!documents || documents.length === 0) ? (
              <Card><CardContent className="p-12 text-center"><FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-lg font-medium text-slate-500">No document requirements</p></CardContent></Card>
            ) : documents.map(doc => (
              <Card key={doc.id} data-testid={`portal-doc-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{doc.document_type}</p>
                      <p className="text-xs text-slate-500">{doc.document_group?.replace(/_/g, ' ')}</p>
                    </div>
                    {doc.mandatory_flag && <Badge className="bg-red-50 text-red-600 text-[9px]">Mandatory</Badge>}
                    <Badge className={`text-[10px] ${DOC_STATUS_COLORS[doc.status] || 'bg-slate-100'}`}>{doc.status?.replace(/_/g, ' ')}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Rectify Dialog with Photo Upload */}
      <Dialog open={showRectify} onOpenChange={setShowRectify}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-bold flex items-center gap-2"><Wrench className="h-5 w-5" /> Mark as Rectified</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-slate-600">{rectifyDefect?.defect_no}</span> — {rectifyDefect?.description_short}
          </DialogDescription>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Upload Photo Proof</Label>
              <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-blue-300 transition-colors cursor-pointer" onClick={() => document.getElementById('rectify-photo-input')?.click()}>
                {rectifyPhoto ? (
                  <div className="flex items-center gap-2 justify-center">
                    <Camera className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-green-700 font-medium">{rectifyPhoto.name}</span>
                  </div>
                ) : (
                  <>
                    <Camera className="h-8 w-8 text-slate-300 mx-auto mb-1" />
                    <p className="text-sm text-slate-500">Click to upload photo</p>
                    <p className="text-xs text-slate-400">JPG, PNG or PDF</p>
                  </>
                )}
                <Input id="rectify-photo-input" type="file" accept="image/*,.pdf" className="hidden" onChange={e => setRectifyPhoto(e.target.files?.[0] || null)} data-testid="rectify-photo-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Rectification Notes (Optional)</Label>
              <Textarea value={rectifyComment} onChange={e => setRectifyComment(e.target.value)} rows={3} placeholder="Describe what was done to rectify this defect..." className="mt-1" data-testid="rectify-comment-input" />
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowRectify(false)}>Cancel</Button>
            <Button className="bg-[#1e3a5f] hover:bg-[#16304d]" onClick={handleRectifySubmit} disabled={rectifyUploading} data-testid="rectify-submit-btn">
              {rectifyUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Confirm Rectified
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={showComment} onOpenChange={setShowComment}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-bold">Add Comment</DialogTitle>
          <DialogDescription>Your comment will be visible to the project team.</DialogDescription>
          <Textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Describe the progress, request clarification, or provide additional details..." rows={4} data-testid="portal-comment-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComment(false)}>Cancel</Button>
            <Button className="bg-[#1e3a5f] hover:bg-[#16304d]" onClick={handleAddComment} data-testid="portal-comment-submit"><Send className="h-4 w-4 mr-1" /> Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="bg-slate-100 border-t mt-12">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between text-sm text-slate-500">
          <div className="flex items-center gap-1.5"><Shield className="h-4 w-4" /><span>Secure Portal Access</span></div>
          <span>Gantt ACC</span>
        </div>
      </div>
    </div>
  );
};

export default CloseoutPortalPage;
